import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { Terminal } from '@mui/icons-material';
import { getConnections as getDevopsConnections, executeCommand, getAdhocJobOutput } from '../services/devopsAPIService';

// Logs polling now uses real adhoc output API

const ExecuteCustomCommands = () => {
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logsHtml, setLogsHtml] = useState('');
  const [form, setForm] = useState({ command: '', server: '' });

  const logRef = useRef(null);
  const pollRef = useRef(null);

  const defaultLogsText = 'Fetching Logs ...';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const c = await getDevopsConnections();
        if (!mounted) return;
        setConnections(Array.isArray(c) ? c : []);
      } catch (e) {
        if (!mounted) return;
        setConnections([]);
        setSnackbar({ open: true, message: e?.message || 'Failed to load connections', severity: 'error' });
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logsHtml]);

  const canExecute = useMemo(() => form.command.trim() !== '' && !!form.server, [form]);

  const startPollingLogs = (jobId) => {
    setLogsHtml(defaultLogsText);
    const intervalMs = 3000;
    const run = async () => {
      try {
        const out = await getAdhocJobOutput(jobId, 'json');
        const job = out?.job || {};
        const log = out?.log || {};
        const content = (typeof log?.content === 'string') ? log.content : '';
        const statusLine = `Job ${jobId}: ${job.status || 'pending'}${job.elapsedTime != null ? ` | t+${Math.floor(job.elapsedTime)}s` : ''}`;
        setLogsHtml((_) => `${statusLine}${content ? `<br/>${content.replace(/\n/g, '<br/>')}` : ''}`);
        const finished = (job.status || '').toLowerCase() === 'successful' || (job.status || '').toLowerCase() === 'failed' || (job.status || '').toLowerCase() === 'error' || (job.finishedAt != null);
        if (finished) {
          setIsLoading(false);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        setIsLoading(false);
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSnackbar({ open: true, message: e?.message || 'Failed to fetch logs', severity: 'error' });
      }
    };
    run();
    pollRef.current = setInterval(run, intervalMs);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleExecute = async () => {
    if (!canExecute) return;
    setIsLoading(true);
    setSnackbar({ open: false, message: '', severity: 'success' });
    try {
      const requestBody = {
        command: form.command,
        connectionId: form.server,
      };
      const res = await executeCommand(requestBody);
      const jobId = res?.jobId || res?.data?.jobId || res?.data?.job?.jobId;
      setSnackbar({ open: true, message: 'Execution started. Please keep this page open to view logs.', severity: 'success' });
      if (jobId) {
        startPollingLogs(jobId);
      } else {
        setIsLoading(false);
        setSnackbar({ open: true, message: 'No job id returned from server', severity: 'error' });
      }
    } catch (e) {
      setIsLoading(false);
      setSnackbar({ open: true, message: e?.message || 'Failed to start execution. Try again.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Execute Custom Commands</Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <TextField
          fullWidth
          size="small"
          label="Command / Script"
          placeholder="e.g., sudo systemctl status nginx"
          value={form.command}
          onChange={(e) => setForm((p) => ({ ...p, command: e.target.value }))}
        />

        <FormControl fullWidth size="small" required>
          <InputLabel>Server</InputLabel>
          <Select
            label="Server"
            value={form.server}
            onChange={(e) => setForm((p) => ({ ...p, server: e.target.value }))}
          >
            {connections.map((c) => (
              <MenuItem key={c.connectionId} value={c.connectionId}>
                {c.connectionName} ({c.hostName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleExecute}
          disabled={!canExecute || isLoading}
          startIcon={<Terminal />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            width: { xs: '100%', md: 'auto' },
            alignSelf: { xs: 'stretch', md: 'auto' },
            px: 2.5,
            minWidth: { md: 160 },
          }}
        >
          {isLoading ? 'Executing...' : 'Execute'}
        </Button>
      </Stack>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Logs</Typography>
        <Paper
          variant="outlined"
          sx={{ p: 2, height: 320, overflowY: 'auto', borderRadius: 2, bgcolor: '#0b1220', color: '#e5e7eb' }}
          ref={logRef}
        >
          <pre style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: logsHtml }} />
        </Paper>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExecuteCustomCommands;
